import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../utils/echarts'
import styled from 'styled-components'
import { Loading } from '../../atoms/Loading'
import { Flex } from '../../atoms/Flex'
import { Box } from '../../atoms/Box'
import {
  UnitCompositionChartProps,
  DEFAULT_UNIT_COMPOSITION_CHART_LABELS,
} from './UnitCompositionChart.types'

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const ChartWrapper = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }) => $height}px;
`

const ErrorContainer = styled(Flex)`
  height: 350px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const LoadingContainer = styled(Flex)`
  height: 350px;
`

const ChartsGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`

export interface UnitCompositionChartRef {
  exportChart: () => string | null
}

export const UnitCompositionChart = forwardRef<
  UnitCompositionChartRef,
  UnitCompositionChartProps
>(
  (
    {
      roomDistributionData = [],
      unitTypeData = [],
      loading = false,
      error = null,
      height = 400,
      labels = {},
    },
    ref
  ) => {
    const mergedLabels = { ...DEFAULT_UNIT_COMPOSITION_CHART_LABELS, ...labels }
    const roomChartRef = useRef<HTMLDivElement>(null)
    const treemapChartRef = useRef<HTMLDivElement>(null)
    const roomChartInstance = useRef<echarts.ECharts | null>(null)
    const treemapChartInstance = useRef<echarts.ECharts | null>(null)

    useImperativeHandle(ref, () => ({
      exportChart: () => {
        if (roomChartInstance.current) {
          return roomChartInstance.current.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#fff',
          })
        }
        return null
      },
    }))

    useEffect(() => {
      if (
        !loading &&
        !error &&
        roomDistributionData.length > 0 &&
        roomChartRef.current
      ) {
        initializeRoomChart()
      }

      return () => {
        roomChartInstance.current?.dispose()
      }
    }, [roomDistributionData, loading, error])

    useEffect(() => {
      if (
        !loading &&
        !error &&
        unitTypeData.length > 0 &&
        treemapChartRef.current
      ) {
        initializeTreemapChart()
      }

      return () => {
        treemapChartInstance.current?.dispose()
      }
    }, [unitTypeData, loading, error])

    useEffect(() => {
      const handleResize = () => {
        roomChartInstance.current?.resize()
        treemapChartInstance.current?.resize()
      }

      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    const initializeRoomChart = () => {
      if (
        !roomChartRef.current ||
        !roomDistributionData ||
        roomDistributionData.length === 0
      )
        return

      if (roomChartInstance.current) {
        roomChartInstance.current.dispose()
      }

      roomChartInstance.current = safeChartInit(roomChartRef.current)

      // Sort by room count
      const sortedData = [...roomDistributionData].sort(
        (a, b) => a.room_count - b.room_count
      )

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.roomDistribution,
          subtext: mergedLabels.roomDistributionSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter: (params: any) => {
            const param = params[0]
            return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 4px;">${param.axisValue} ${param.axisValue === '1' ? mergedLabels.room : mergedLabels.rooms}</div>
              <div>${mergedLabels.numberOfUnits}: ${param.value.toLocaleString()}</div>
            </div>
          `
          },
        },
        grid: {
          left: '10%',
          right: '10%',
          top: '22%',
          bottom: '15%',
        },
        xAxis: {
          type: 'category',
          data: sortedData.map((d) => d.room_count.toString()),
          name: mergedLabels.numberOfRooms,
          nameLocation: 'middle',
          nameGap: 30,
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
        },
        yAxis: {
          type: 'value',
          name: mergedLabels.numberOfUnits,
          nameTextStyle: {
            fontSize: 12,
            fontWeight: 600,
          },
          axisLabel: {
            formatter: (value: number) => value.toLocaleString(),
          },
        },
        series: [
          {
            type: 'bar',
            data: sortedData.map((d) => d.unit_count),
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#5470c6' },
                { offset: 1, color: '#91cc75' },
              ]),
            },
            emphasis: {
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#4460b6' },
                  { offset: 1, color: '#81bc65' },
                ]),
              },
            },
            label: {
              show: true,
              position: 'top',
              formatter: (params: any) => params.value.toLocaleString(),
              fontSize: 10,
            },
          },
        ],
      }

      roomChartInstance.current.setOption(option)
    }

    const initializeTreemapChart = () => {
      if (
        !treemapChartRef.current ||
        !unitTypeData ||
        unitTypeData.length === 0
      )
        return

      if (treemapChartInstance.current) {
        treemapChartInstance.current.dispose()
      }

      treemapChartInstance.current = safeChartInit(treemapChartRef.current)

      // Sort by unit count descending
      const sortedData = [...unitTypeData].sort(
        (a, b) => b.unit_count - a.unit_count
      )

      // Prepare treemap data
      const treemapData = sortedData.map((item) => ({
        name: item.unit_type,
        value: item.unit_count,
        itemStyle: {
          // Color based on average area (darker = larger area)
          color:
            item.avg_area > 100
              ? '#5470c6'
              : item.avg_area > 50
                ? '#91cc75'
                : '#fac858',
        },
      }))

      const option: echarts.EChartsOption = {
        title: {
          text: mergedLabels.unitTypeComposition,
          subtext: mergedLabels.unitTypeCompositionSubtext,
          left: 'center',
          top: 10,
          textStyle: {
            fontSize: 16,
            fontWeight: 600,
          },
          subtextStyle: {
            fontSize: 12,
          },
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            const dataItem = sortedData.find((d) => d.unit_type === params.name)
            if (!dataItem) return ''
            return `
            <div style="padding: 8px;">
              <div style="font-weight: 600; margin-bottom: 8px;">${params.name}</div>
              <div>${mergedLabels.numberOfUnits}: ${dataItem.unit_count.toLocaleString()}</div>
              <div>${mergedLabels.averageArea}: ${Math.round(dataItem.avg_area).toLocaleString()} m²</div>
            </div>
          `
          },
        },
        series: [
          {
            type: 'treemap',
            data: treemapData,
            top: '18%',
            left: '5%',
            right: '5%',
            bottom: '5%',
            roam: false,
            nodeClick: false,
            breadcrumb: {
              show: false,
            },
            label: {
              show: true,
              formatter: (params: any) => {
                return `{name|${params.name}}\n{value|${params.value.toLocaleString()} ${mergedLabels.units}}`
              },
              rich: {
                name: {
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  lineHeight: 20,
                },
                value: {
                  fontSize: 11,
                  color: '#fff',
                  lineHeight: 18,
                },
              },
            },
            upperLabel: {
              show: false,
            },
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 2,
              gapWidth: 2,
            },
            emphasis: {
              itemStyle: {
                borderColor: '#333',
                borderWidth: 3,
              },
              label: {
                show: true,
              },
            },
            levels: [
              {
                itemStyle: {
                  borderWidth: 3,
                  gapWidth: 3,
                },
              },
            ],
          },
        ],
      }

      treemapChartInstance.current.setOption(option)
    }

    if (loading) {
      return (
        <LoadingContainer justify="center" align="center">
          <Loading size="large" />
        </LoadingContainer>
      )
    }

    if (error) {
      return (
        <ErrorContainer justify="center" align="center">
          {error}
        </ErrorContainer>
      )
    }

    if (roomDistributionData.length === 0 && unitTypeData.length === 0) {
      return (
        <ErrorContainer justify="center" align="center">
          {mergedLabels.noData}
        </ErrorContainer>
      )
    }

    return (
      <ChartContainer>
        <ChartsGrid>
          {roomDistributionData.length > 0 && (
            <ChartWrapper $height={height} ref={roomChartRef} />
          )}
          {unitTypeData.length > 0 && (
            <ChartWrapper $height={height} ref={treemapChartRef} />
          )}
        </ChartsGrid>
      </ChartContainer>
    )
  }
)

UnitCompositionChart.displayName = 'UnitCompositionChart'
